import { RealPhaseEvidence } from "@/components/installations/RealPhaseEvidence";
import { CalendarWorkFinder } from "@/components/CalendarWorkFinder";
import { CalendarEventCard } from "./CalendarEventCard";
import { indexCalendarWorks, parseWorkId, searchCalendarWorks, type WorkSearchEvent } from "./calendarWorkSearch";
import { useAuth } from "@/_core/hooks/useAuth";
import DashboardLayout from "@/components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { trpc } from "@/lib/trpc";
import { useState, useMemo, useEffect } from "react";
import { Calendar as BigCalendar, dateFnsLocalizer, View, Views } from "react-big-calendar";
import { format, parse, startOfWeek, getDay, setMonth, setYear, isSameMonth } from "date-fns";
import { es } from "date-fns/locale";
import { useLocation, useSearch } from "wouter";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight, ChevronDown, ArrowRight, Clock, User, MapPin, Download } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import {
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    BreadcrumbList,
    BreadcrumbPage,
    BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { getCalendarEventInstallationPathFromCalendar, formatCalendarDateParam, parseCalendarDateParam, parseCalendarLayout } from "./calendarEventNavigation";
import { shiftCalendarDate } from "./calendarNavigation";
import { getLocalDateStr } from "@/lib/utils";
import { resolveLaborDayMinutes } from "../../../shared/laborDuration";
import {
    getInstallationColor,
    formatContractDeadlineDetails,
    isContractualPlanningCommitment,
    getPlanningEventStyle,
    getCalendarEventStyle,
    getStatusPresentation,
    canViewTechnicianFilter,
    isExecutionEventVisible,
    getDayViewBreadcrumbInfo,
    filterEventsForDate,
    parseCalendarViewParam,
    getCalendarViewParam,
    getRealStatusPresentation,
    formatRealTimestampLabel,
    buildRealCalendarEvents,
    resolveLaboralEventTimes,
    formatLaboralMinutes,
    summarizeLaboralMonthByTechnician,
    canViewMonthlyLaborSummary,
    formatLaborProvenanceLabel,
    formatLaborPercent,
    formatLaborInstallationIdentity,
    reconcileMonthlyLaborSummary,
    triggerCsvDownload,
    monthlyLaborCsvFilename,
    type CalendarViewMode,
} from "./calendarUtils";
import "./calendar.css";

const locales = { 'es': es };

const localizer = dateFnsLocalizer({ format, parse, startOfWeek, getDay, locales });

const messages = {
    allDay: 'Todo el día', previous: 'Anterior', next: 'Siguiente', today: 'Hoy',
    month: 'Mes', week: 'Semana', day: 'Día', agenda: 'Agenda',
    date: 'Fecha', time: 'Hora', event: 'Evento', noEventsInRange: 'No hay eventos en este rango',
    showMore: (total: number) => `+${total} más`,
};

// Year View Component
const YearView = ({ date, events, onNavigate, onView }: { date: Date; events: any[]; onNavigate: (date: Date) => void; onView: (view: View) => void; }) => {
    const months = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
    return (
        <div className="year-view-grid">
            {months.map((month, index) => {
                const monthDate = setMonth(date, index);
                const monthEvents = events.filter(e => isSameMonth(e.start, monthDate) && e.start.getFullYear() === date.getFullYear());
                return (
                    <div key={month} className="border rounded-lg p-4 hover:border-blue-500 cursor-pointer transition-colors bg-white shadow-sm"
                        onClick={() => { onNavigate(monthDate); onView(Views.MONTH); }}>
                        <div className="font-semibold text-lg mb-2 text-gray-700">{month}</div>
                        <div className="text-sm text-gray-500 mb-2">{monthEvents.length} instalaciones</div>
                        <div className="space-y-1">
                            {monthEvents.slice(0, 3).map(e => (
                                <div key={e.id} className="text-xs truncate px-2 py-1 rounded text-white" style={e.resource?.style}>{e.title}</div>
                            ))}
                            {monthEvents.length > 3 && <div className="text-xs text-gray-400 pl-1">+{monthEvents.length - 3} más...</div>}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export default function CalendarPage() {
    const { user } = useAuth();
    const [, setLocation] = useLocation();
    const searchString = useSearch();
    const params = new URLSearchParams(searchString);

    // CAL-08: three unambiguous views. Legacy links preserved:
    // view=planning → Programación, view=execution → Laboral, view=real → Real (canonical).
    const initialMode = parseCalendarViewParam(params.get("view"));
    const [calendarMode, setCalendarMode] = useState<CalendarViewMode>(initialMode);

    // CAL-07: restore recoverable calendar context (month/date + technician filter)
    // from query params when coming back from installation detail.
    const [selectedTechnician, setSelectedTechnician] = useState<string>(() => {
        const tech = params.get("tech");
        return tech && (tech === "all" || /^\d+$/.test(tech)) ? tech : "all";
    });
    const [currentDate, setCurrentDate] = useState<Date>(() => parseCalendarDateParam(params.get("date")) ?? new Date());
    const [view, setView] = useState<View>(() => parseCalendarLayout(params.get("layout")));
    const [workQuery, setWorkQuery] = useState(() => (params.get("q") ?? "").slice(0, 200));
    const [selectedWorkId, setSelectedWorkId] = useState(() => parseWorkId(params.get("work")));
    const [showYearView, setShowYearView] = useState(false);

    // State for accessible Day Drawer / Popover
    const [dayDrawerState, setDayDrawerState] = useState<{
        isOpen: boolean;
        date: Date | null;
        events: any[];
    }>({
        isOpen: false,
        date: null,
        events: [],
    });

    // Sync query params on mode/date/filter change (writes the canonical view value)
    // CAL-07: keeps the full context recoverable by installation detail's back link.
    useEffect(() => {
        const newParams = new URLSearchParams(searchString);
        newParams.set("view", getCalendarViewParam(calendarMode));
        newParams.set("date", formatCalendarDateParam(currentDate));
        newParams.set("tech", selectedTechnician);
        newParams.set("layout", parseCalendarLayout(view));
        if (workQuery) newParams.set("q", workQuery); else newParams.delete("q");
        if (selectedWorkId) newParams.set("work", String(selectedWorkId)); else newParams.delete("work");
        window.history.replaceState(null, "", `?${newParams.toString()}`);
    }, [calendarMode, currentDate, selectedTechnician, view, workQuery, selectedWorkId]);

    // Role check for technician filter authorization (includes admin, project_manager, admin_manager)
    const canFilterTechnicians = canViewTechnicianFilter(user?.role);

    // Queries
    // Same authorized installation endpoint supplies names/codes in all views.
    const installationsQuery = trpc.installations.list.useQuery();
    const techniciansQuery = trpc.users.listTechnicians.useQuery(undefined, {
        enabled: canFilterTechnicians,
    });
    // Laboral view applies the technician filter; Real view always aggregates full
    // evidence and never reuses Laboral filter semantics.
    const executionQuery = trpc.executionCalendar.list.useQuery(
        calendarMode === "real"
            ? {}
            : selectedTechnician !== "all"
                ? { technicianId: parseInt(selectedTechnician) }
                : {},
        {
            enabled: calendarMode === "laboral" || calendarMode === "real",
            refetchInterval: calendarMode === "laboral" ? 15000 : false,
        }
    );

    const realPhasesQuery = trpc.realCalendar.phases.useQuery(undefined, { enabled: calendarMode === "real" });

    // ========== PLANNING MODE ==========
    const filteredInstallations = useMemo(() => {
        if (calendarMode !== "planning" || !installationsQuery.data) return [];
        if (selectedTechnician === "all") return installationsQuery.data;
        return installationsQuery.data.filter((inst: { assignedTechnicianIds: any[] | null }) => {
            const assignedIds = inst.assignedTechnicianIds as any[] | null;
            return Array.isArray(assignedIds) && assignedIds.map(id => Number(id)).includes(parseInt(selectedTechnician));
        });
    }, [installationsQuery.data, selectedTechnician, calendarMode]);

    const planningEvents = useMemo(() => {
        if (calendarMode !== "planning" || !filteredInstallations) return [];

        const events: any[] = [];
        filteredInstallations.forEach((inst: any) => {
            const segments = inst.planningSegments || [];
            const contractDeadline = inst.contractualEndDate || null;
            const isContractual = isContractualPlanningCommitment(inst);
            const contractDeadlineDetails = formatContractDeadlineDetails(inst);
            const installationColor = getInstallationColor(inst.id);
            const statusInfo = getStatusPresentation("planning", inst.status);

            segments.forEach((seg: any) => {
                if (!seg.startDate || !seg.endDate) return;
                const sParts = String(seg.startDate).split('T')[0].split('-').map(Number);
                const eParts = String(seg.endDate).split('T')[0].split('-').map(Number);
                if (sParts.length < 3 || eParts.length < 3) return;

                const startDate = new Date(sParts[0], sParts[1] - 1, sParts[2], 0, 0, 0);
                // For react-big-calendar month/day allDay rendering, end date is exclusive.
                // Add 1 day ONLY for render so single-day phase (e.g. May 1 to May 1) occupies 1 day.
                const renderEndDate = new Date(eParts[0], eParts[1] - 1, eParts[2] + 1, 0, 0, 0);

                events.push({
                    id: `seg-${seg.id}`,
                    title: `${contractDeadlineDetails ? '⚠ ' : ''}${inst.clientName || 'Instalación'} - ${seg.title || 'Fase'}`,
                    start: startDate,
                    end: renderEndDate,
                    allDay: true,
                    resource: {
                        installationId: inst.id,
                        segmentId: seg.id,
                        clientName: inst.clientName,
                        phaseTitle: seg.title,
                        description: seg.description,
                        startDate: seg.startDate,
                        endDate: seg.endDate,
                        status: inst.status,
                        statusLabel: statusInfo.label,
                        statusColor: statusInfo.dotColor,
                        statusBadgeClass: statusInfo.badgeClass,
                        workOrderType: inst.workOrderType,
                        isContractual,
                        contractCode: inst.contractCode || null,
                        contractualEndDate: contractDeadline,
                        contractId: inst.contractId,
                        installationColor,
                        style: getPlanningEventStyle(installationColor, isContractual),
                    },
                });
            });
        });
        return events;
    }, [filteredInstallations, calendarMode]);

    // ========== LABORAL MODE (actual time, plans and unverified legacy separated) ==========
    const laboralEvents = useMemo(() => {
        if (calendarMode !== "laboral" || !executionQuery.data) return [];
        const todayStr = getLocalDateStr();

        return executionQuery.data
            .filter((evt: any) => isExecutionEventVisible(evt.date, evt.status, todayStr))
            .map((evt: any) => {
                const times = resolveLaboralEventTimes(evt);
                const resolution = resolveLaborDayMinutes(evt);
                const realTimeLabel = times.realTimeLabel;
                const durationMinutes = resolution.durationVerified ? resolution.minutes : null;
                const durationLabel = resolution.planned
                    ? "Planificada"
                    : durationMinutes !== null
                        ? formatLaboralMinutes(durationMinutes)
                        : resolution.source === "equivalent_day"
                            ? "Equivalencia histórica no verificada (08:00–17:00) · " + formatLaboralMinutes(resolution.equivalentMinutes)
                            : "Duración no verificada";
                const installationColor = getInstallationColor(evt.installationId);
                const statusInfo = getStatusPresentation("laboral", evt.status);
                const identity = formatLaborInstallationIdentity(evt);

                return {
                    id: "exec-" + evt.id,
                    title: identity.primary + " · " + durationLabel,
                    start: times.start,
                    end: times.end,
                    allDay: times.allDay,
                    resource: {
                        id: evt.id,
                        status: evt.status,
                        statusLabel: statusInfo.label,
                        statusColor: statusInfo.dotColor,
                        statusBadgeClass: statusInfo.badgeClass,
                        installationId: evt.installationId,
                        clientName: evt.clientName,
                        siteLabel: identity.primary,
                        siteContext: identity.secondary,
                        installationCode: evt.installationCode ?? null,
                        workDescription: evt.workDescription ?? null,
                        durationLabel,
                        technicianName: evt.technicianName,
                        address: evt.address,
                        totalMinutes: evt.totalMinutes,
                        durationMinutes,
                        durationVerified: resolution.durationVerified,
                        durationSource: resolution.source,
                        equivalentMinutes: resolution.equivalentMinutes,
                        planned: resolution.planned,
                        realTimeLabel,
                        vehicleLabel: evt.shiftVehicleLabel ?? null,
                        realStartTime: times.realStartTime,
                        realEndTime: times.realEndTime,
                        installationColor,
                        style: { backgroundColor: installationColor },
                    },
                };
            });
    }, [executionQuery.data, calendarMode]);

    // CAL-05: basic monthly summary by person (workday evidence only — hours,
    // minutes and days; never economic amounts).
    const laboralMonthSummary = useMemo(() => {
        if (calendarMode !== "laboral") return [];
        return summarizeLaboralMonthByTechnician(executionQuery.data ?? [], currentDate);
    }, [executionQuery.data, calendarMode, currentDate]);

    // CAL-09: monthly labor summary read model (management-only, time only).
    // month -> technician -> installations -> days -> hours/minutes -> percent -> total.
    const canViewLaborSummary = canViewMonthlyLaborSummary(user?.role);
    const laborMonthParam = format(currentDate, 'yyyy-MM');
    const laborSummaryInput = {
        month: laborMonthParam,
        ...(selectedTechnician !== 'all' ? { technicianId: Number(selectedTechnician) } : {}),
    };
    const laborSummaryQuery = trpc.executionCalendar.monthlySummary.useQuery(
        laborSummaryInput,
        { enabled: calendarMode === "laboral" && canViewLaborSummary, refetchInterval: 15000 }
    );
    const trpcUtils = trpc.useUtils();

    const handleExportLaborSummaryCsv = async () => {
        const result = await trpcUtils.executionCalendar.monthlySummaryCsv.fetch(laborSummaryInput);
        triggerCsvDownload(result.filename || monthlyLaborCsvFilename(result.month), result.csv);
    };

    // ========== REAL MODE (explicit execution evidence per installation, CAL-08) ==========
    const realEvents = useMemo(() => {
        if (calendarMode !== "real" || !installationsQuery.data) return [];
        return buildRealCalendarEvents(installationsQuery.data.map((inst: any) => ({ ...inst,
            planningSegments: (realPhasesQuery.data ?? []).filter((phase: any) => phase.installationId === inst.id),
        })), executionQuery.data ?? []);
    }, [installationsQuery.data, executionQuery.data, realPhasesQuery.data, calendarMode]);

    const viewEvents = calendarMode === "planning"
        ? planningEvents
        : calendarMode === "real"
            ? realEvents
            : laboralEvents;
    const works = useMemo(() => indexCalendarWorks(viewEvents,
        calendarMode === "planning" ? filteredInstallations : installationsQuery.data ?? []),
        [viewEvents, filteredInstallations, installationsQuery.data, calendarMode]);
    const workResults = useMemo(() => searchCalendarWorks(works, workQuery, selectedWorkId), [works, workQuery, selectedWorkId]);
    const events = selectedWorkId ? viewEvents.filter((event: WorkSearchEvent) => Number(event.resource?.installationId) === selectedWorkId) : viewEvents;
    const workSearchLoading = installationsQuery.isLoading
        || (calendarMode !== "planning" && executionQuery.isLoading)
        || (calendarMode === "real" && realPhasesQuery.isLoading);
    const workSearchError = installationsQuery.isError
        || (calendarMode !== "planning" && executionQuery.isError)
        || (calendarMode === "real" && realPhasesQuery.isError);

    const eventStyleGetter = (event: any) => ({ style: getCalendarEventStyle(event) });

    const handleNavigate = (newDate: Date) => setCurrentDate(newDate);
    const handleDateSelect = (value: string) => {
        const [year, month, day] = value.split("-").map(Number);
        if (!year || !month || !day) return;
        const selected = new Date(year, month - 1, day);
        if (selected.getFullYear() === year && selected.getMonth() === month - 1 && selected.getDate() === day) {
            setCurrentDate(selected);
        }
    };
    const handleYearSelect = (year: number) => setCurrentDate(setYear(currentDate, year));
    const handleMonthSelect = (monthIndex: number) => setCurrentDate(setMonth(currentDate, monthIndex));

    const currentYear = new Date().getFullYear();
    const years = Array.from({ length: 5 }, (_, i) => currentYear - 2 + i);
    const monthNames = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];

    // CAL-07: carry the recoverable calendar context into the detail link so the
    // back action can restore view, month/date and technician filter.
    const handleEventClick = (event: any) => {
        const installationPath = getCalendarEventInstallationPathFromCalendar(event, calendarMode, {
            currentDate,
            technicianFilter: selectedTechnician,
            layout: parseCalendarLayout(view),
            workId: selectedWorkId,
            workQuery,
        });
        if (installationPath) setLocation(installationPath);
    };

    // Month view "+N más" drawer trigger
    const handleShowMore = (moreEvents: any[], date: Date) => {
        const allDayEvents = filterEventsForDate(events, date);
        setDayDrawerState({
            isOpen: true,
            date,
            events: allDayEvents.length > 0 ? allDayEvents : moreEvents,
        });
    };

    const dayBreadcrumbInfo = getDayViewBreadcrumbInfo(currentDate);

    return (
        <DashboardLayout>
            <div className="flex flex-col h-full gap-4 p-4">
                <div className="flex flex-col xl:flex-row xl:flex-wrap justify-between items-start gap-4">
                    <div>
                        <h1 className="text-3xl font-bold tracking-tight">Calendario</h1>
                        <p className="text-muted-foreground">
                            {calendarMode === "planning"
                                ? "Programación teórica de instalaciones"
                                : calendarMode === "real"
                                    ? "Real de obras — evidencia de ejecución por instalación y fase"
                                    : "Laboral — jornadas y asignaciones de técnicos"}
                        </p>
                    </div>

                    <div className="flex w-full flex-wrap items-center gap-2 xl:w-auto">
                        {/* Mode Toggle (CAL-08: three unambiguous views) */}
                        <div className="flex w-full flex-wrap gap-2 sm:w-auto">
                            <Button
                                size="lg"
                                variant={calendarMode === 'planning' ? 'default' : 'outline'}
                                className={`w-full px-3 py-2 text-sm font-semibold sm:w-auto sm:px-4 sm:py-3 sm:text-base ${calendarMode === 'planning' ? '' : 'border-2'}`}
                                onClick={() => setCalendarMode('planning')}
                            >
                                Programación
                            </Button>
                            <Button
                                size="lg"
                                variant={calendarMode === 'real' ? 'default' : 'outline'}
                                className={`w-full px-3 py-2 text-sm font-semibold sm:w-auto sm:px-4 sm:py-3 sm:text-base ${calendarMode === 'real' ? '' : 'border-2'}`}
                                onClick={() => setCalendarMode('real')}
                            >
                                Real de obras
                            </Button>
                            <Button
                                size="lg"
                                variant={calendarMode === 'laboral' ? 'laborAction' : 'outline'}
                                className={`w-full px-3 py-2 text-sm font-semibold sm:w-auto sm:px-4 sm:py-3 sm:text-base ${calendarMode === 'laboral' ? '' : 'border-2'}`}
                                onClick={() => setCalendarMode('laboral')}
                            >
                                Laboral / Técnicos
                            </Button>
                        </div>

                        {/* Technician Filter: Laboral/Planning semantics only — never in Real view */}
                        {canFilterTechnicians && calendarMode !== "real" && (
                            <div className="w-full sm:w-[200px]">
                                <Select value={selectedTechnician} onValueChange={setSelectedTechnician}>
                                    <SelectTrigger>
                                        <SelectValue placeholder="Filtrar por técnico" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">Todos los técnicos</SelectItem>
                                        {techniciansQuery.data?.map((tech: { id: number; name: string }) => (
                                            <SelectItem key={tech.id} value={tech.id.toString()}>{tech.name}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}

                        {/* Year View Toggle (planning only) */}
                        {calendarMode === "planning" && (
                            <Button variant={showYearView ? "default" : "outline"}
                                className="w-full sm:w-auto" onClick={() => setShowYearView(!showYearView)}>
                                {showYearView ? "Vista Mensual" : "Vista Anual"}
                            </Button>
                        )}
                    </div>
                </div>

                <CalendarWorkFinder query={workQuery} selectedId={selectedWorkId}
                    modeLabel={calendarMode === "planning" ? "Programación" : calendarMode === "real" ? "Real" : "Laboral"}
                    results={workResults} loading={workSearchLoading} error={workSearchError}
                    onQuery={setWorkQuery} onClear={() => { setWorkQuery(""); setSelectedWorkId(null); }}
                    onChoose={(id, date) => { setSelectedWorkId(id); setCurrentDate(date); }} />

                {calendarMode === "real" && <>
                    {(realPhasesQuery.error || executionQuery.error) && <p role="alert">No se pudo cargar la evidencia real. Recarga antes de interpretar datos incompletos.</p>}
                    <RealPhaseEvidence installations={installationsQuery.data ?? []} phases={realPhasesQuery.data ?? []}
                        sessions={executionQuery.data ?? []} canEdit={canFilterTechnicians}
                        onSaved={() => { void realPhasesQuery.refetch(); void executionQuery.refetch(); }} />
                </>}

                <Card className="flex-1 min-h-[700px]">
                    <CardHeader className="flex flex-col gap-3 space-y-0 pb-3 xl:flex-row xl:flex-wrap xl:items-center xl:justify-between border-b">
                        <CardTitle className="w-full min-w-0 text-xl font-bold xl:w-auto">
                            {!showYearView ? (
                                <div className="flex flex-wrap items-center gap-2">
                                    <Button variant="outline" size="icon" aria-label="Periodo anterior" onClick={() => handleNavigate(shiftCalendarDate(currentDate, parseCalendarLayout(view), -1))}>
                                        <ChevronLeft className="h-4 w-4" />
                                    </Button>

                                    <Popover>
                                        <PopoverTrigger asChild>
                                            <Button variant="ghost" className="text-lg font-semibold">
                                                <CalendarIcon className="mr-2 h-5 w-5" />
                                                {format(currentDate, 'MMMM yyyy', { locale: es })}
                                            </Button>
                                        </PopoverTrigger>
                                        <PopoverContent className="w-auto p-4" align="start">
                                            <div className="space-y-4">
                                                <div className="grid grid-cols-3 gap-2">
                                                    {monthNames.map((m, i) => (
                                                        <Button key={m} variant={currentDate.getMonth() === i ? (calendarMode === "laboral" ? "laborAction" : "default") : "ghost"} size="sm" onClick={() => handleMonthSelect(i)}>
                                                             {m.substring(0, 3)}
                                                        </Button>
                                                    ))}
                                                </div>
                                                <div className="border-t pt-2 grid grid-cols-5 gap-1">
                                                    {years.map(y => (
                                                        <Button key={y} variant={currentDate.getFullYear() === y ? (calendarMode === "laboral" ? "laborAction" : "default") : "ghost"} size="sm" onClick={() => handleYearSelect(y)}>
                                                            {y}
                                                        </Button>
                                                    ))}
                                                </div>
                                            </div>
                                        </PopoverContent>
                                    </Popover>

                                    <Button variant="outline" size="icon" aria-label="Periodo siguiente" onClick={() => handleNavigate(shiftCalendarDate(currentDate, parseCalendarLayout(view), 1))}>
                                        <ChevronRight className="h-4 w-4" />
                                    </Button>
                                    <label className="flex items-center gap-2 rounded-md border px-2 h-9 text-sm font-normal">
                                        <span className="sr-only">Seleccionar fecha</span>
                                        <CalendarIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
                                        <input
                                            aria-label="Seleccionar fecha"
                                            type="date"
                                            className="w-[8.5rem] bg-transparent text-sm outline-none"
                                            value={formatCalendarDateParam(currentDate)}
                                            onChange={(event) => handleDateSelect(event.target.value)}
                                        />
                                    </label>
                                    <Button variant="outline" size="sm" onClick={() => handleNavigate(new Date())}>Hoy</Button>

                                    {/* View Selector Switcher */}
                                    <div className="flex items-center rounded-md border p-0.5 bg-muted/40 ml-2">
                                        <Button
                                            variant={view === Views.MONTH && !showYearView ? (calendarMode === "laboral" ? "laborAction" : "default") : "ghost"}
                                            size="sm"
                                            className="h-8 px-2.5 text-xs font-medium"
                                            onClick={() => { setView(Views.MONTH); setShowYearView(false); }}
                                        >
                                            Mes
                                        </Button>
                                        <Button
                                            variant={view === Views.WEEK && !showYearView ? (calendarMode === "laboral" ? "laborAction" : "default") : "ghost"}
                                            size="sm"
                                            className="h-8 px-2.5 text-xs font-medium"
                                            onClick={() => { setView(Views.WEEK); setShowYearView(false); }}
                                        >
                                            Semana
                                        </Button>
                                        <Button
                                            variant={view === Views.DAY && !showYearView ? (calendarMode === "laboral" ? "laborAction" : "default") : "ghost"}
                                            size="sm"
                                            className="h-8 px-2.5 text-xs font-medium"
                                            onClick={() => { setView(Views.DAY); setShowYearView(false); }}
                                        >
                                            Día
                                        </Button>
                                        <Button
                                            variant={view === Views.AGENDA && !showYearView ? (calendarMode === "laboral" ? "laborAction" : "default") : "ghost"}
                                            size="sm"
                                            className="h-8 px-2.5 text-xs font-medium"
                                            onClick={() => { setView(Views.AGENDA); setShowYearView(false); }}
                                        >
                                            Agenda
                                        </Button>
                                    </div>
                                </div>
                            ) : (
                                <span>Vista Anual {format(currentDate, 'yyyy')}</span>
                            )}
                        </CardTitle>

                        {/* Status Legend */}
                        <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground hidden lg:flex">
                            <div className="flex items-center gap-1.5 font-medium text-foreground">
                                <div className="w-2.5 h-2.5 rounded-full bg-blue-600"></div>
                                <span>Color: Por instalación</span>
                            </div>
                            <span className="text-muted-foreground/50">|</span>
                            <span className="font-medium text-foreground">Estado:</span>
                            {calendarMode === "planning" && (
                                <>
                                    <div className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-amber-500"></div> <span>Pendiente</span></div>
                                    <div className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-blue-500"></div> <span>Iniciada</span></div>
                                    <div className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-emerald-500"></div> <span>Completada</span></div>
                                    <div className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-rose-500"></div> <span>Cancelada</span></div>
                                    <div className="flex items-center gap-1"><div className="w-2.5 h-2 rounded-xs border-l-4 border-l-red-600 bg-gray-200"></div> <span>[C] Contractual</span></div>
                                </>
                            )}
                            {calendarMode === "real" && (
                                <>
                                    <div className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-blue-500"></div> <span>En curso (real)</span></div>
                                    <div className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-emerald-500"></div> <span>Finalizada (real)</span></div>
                                    <span className="italic text-muted-foreground">Evidencia: fechas reales de instalación y fase</span>
                                </>
                            )}
                            {calendarMode === "laboral" && (
                                <>
                                    <div className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-slate-400"></div> <span>Asignado</span></div>
                                    <div className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-blue-500"></div> <span>Trabajando</span></div>
                                    <div className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-amber-500"></div> <span>Pausado</span></div>
                                    <div className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-emerald-500"></div> <span>Jornada terminada</span></div>
                                </>
                            )}
                        </div>
                    </CardHeader>

                    <CardContent className="h-full p-4">
                        {/* Day View Breadcrumb and Return Action */}
                        {!showYearView && view === Views.DAY && (
                            <div className="flex flex-wrap items-center justify-between gap-2 pb-3 mb-3 border-b">
                                <Breadcrumb>
                                    <BreadcrumbList>
                                        <BreadcrumbItem>
                                            <BreadcrumbLink asChild>
                                                <button
                                                    type="button"
                                                    className="cursor-pointer font-medium text-muted-foreground hover:text-foreground transition-colors"
                                                    onClick={() => setView(Views.MONTH)}
                                                >
                                                    {dayBreadcrumbInfo.rootLabel}
                                                </button>
                                            </BreadcrumbLink>
                                        </BreadcrumbItem>
                                        <BreadcrumbSeparator />
                                        <BreadcrumbItem>
                                            <BreadcrumbLink asChild>
                                                <button
                                                    type="button"
                                                    className="cursor-pointer font-medium text-muted-foreground hover:text-foreground transition-colors"
                                                    onClick={() => setView(Views.MONTH)}
                                                >
                                                    {dayBreadcrumbInfo.monthLabel}
                                                </button>
                                            </BreadcrumbLink>
                                        </BreadcrumbItem>
                                        <BreadcrumbSeparator />
                                        <BreadcrumbItem>
                                            <BreadcrumbPage className="font-semibold text-foreground">
                                                {dayBreadcrumbInfo.dayLabel}
                                            </BreadcrumbPage>
                                        </BreadcrumbItem>
                                    </BreadcrumbList>
                                </Breadcrumb>

                                <Button
                                    variant="outline"
                                    size="sm"
                                    className="gap-1.5 text-xs sm:text-sm font-medium"
                                    onClick={() => setView(Views.MONTH)}
                                >
                                    <ChevronLeft className="h-4 w-4" />
                                    {dayBreadcrumbInfo.backActionLabel}
                                </Button>
                            </div>
                        )}

                        {showYearView && calendarMode === "planning" ? (
                            <YearView
                                date={currentDate}
                                events={events}
                                onNavigate={(d) => { handleNavigate(d); setShowYearView(false); }}
                                onView={setView}
                            />
                        ) : (
                            <div
                                className={calendarMode === "laboral" && view === Views.MONTH ? "calendar-labor-month-scroll" : undefined}
                                role={calendarMode === "laboral" && view === Views.MONTH ? "region" : undefined}
                                aria-label={calendarMode === "laboral" && view === Views.MONTH ? "Calendario laboral mensual; en pantallas estrechas desplázate horizontalmente para leer cada sede" : undefined}
                                aria-describedby={calendarMode === "laboral" && view === Views.MONTH ? "calendar-labor-month-scroll-hint" : undefined}
                                tabIndex={calendarMode === "laboral" && view === Views.MONTH ? 0 : undefined}
                            >
                            {calendarMode === "laboral" && view === Views.MONTH && (
                                <p id="calendar-labor-month-scroll-hint" className="calendar-labor-month-scroll-hint">
                                    Desliza horizontalmente para leer las sedes; selecciona un evento para abrir su detalle.
                                </p>
                            )}
                            <BigCalendar
                                localizer={localizer}
                                events={events}
                                startAccessor="start"
                                endAccessor="end"
                                style={{ height: 'calc(100vh - 250px)', minHeight: '600px' }}
                                messages={messages}
                                culture="es"
                                eventPropGetter={eventStyleGetter}
                                onSelectEvent={handleEventClick}
                                onShowMore={handleShowMore}
                                doShowMoreDrillDown={false}
                                components={{
                                    event: CalendarEventCard,
                                }}
                                date={currentDate}
                                onNavigate={handleNavigate}
                                onDrillDown={(date) => { handleNavigate(date); setView(Views.DAY); }}
                                view={view}
                                onView={setView}
                                views={['month', 'week', 'day', 'agenda']}
                                className={calendarMode === "laboral" && view === Views.MONTH
                                    ? "labor-calendar-month"
                                    : calendarMode === "laboral" && view === Views.AGENDA
                                        ? "labor-calendar-agenda"
                                        : undefined}
                                showAllEvents={calendarMode === "laboral" && view === Views.MONTH}
                                toolbar={false}
                            />
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* CAL-09: monthly labor summary (management-only, time only, no economics) */}
                {calendarMode === "laboral" && canViewLaborSummary && (
                    <details className="group rounded-lg border bg-card">
                        <summary className="flex cursor-pointer list-none items-center justify-between gap-3 rounded-md px-3 py-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                            <span className="min-w-0">
                                <span className="block font-semibold">Resumen mensual de mano de obra — {format(currentDate, 'MMMM yyyy', { locale: es })}</span>
                                <span className="block text-xs text-muted-foreground">{laborSummaryQuery.data?.technicians.length ?? 0} técnicos · horas reales verificadas; planificación y equivalencias separadas</span>
                            </span>
                            <span className="flex shrink-0 items-center gap-1 text-sm font-medium text-ods-navigation">
                                <span className="group-open:hidden">Desplegar resumen</span>
                                <span className="hidden group-open:inline">Plegar resumen</span>
                                <ChevronDown aria-hidden="true" className="h-4 w-4 transition-transform group-open:rotate-180" />
                            </span>
                        </summary>
                    <Card className="shrink-0 border-0 shadow-none">
                        <CardHeader className="pb-2">
                            <div className="flex flex-wrap items-start justify-between gap-2">
                                <div>
                                    <CardTitle className="text-base font-semibold">
                                        Resumen mensual de mano de obra — {format(currentDate, 'MMMM yyyy', { locale: es })}
                                    </CardTitle>
                                    <p className="text-xs text-muted-foreground">
                                        Minutos reales verificados por técnico, instalación y día. Planificación y equivalencia histórica no verificada quedan fuera de totales y porcentajes.
                                    </p>
                                </div>
                                <Button
                                    size="sm"
                                    variant="outline"
                                    className="gap-1.5 text-xs"
                                    disabled={laborSummaryQuery.isLoading || !laborSummaryQuery.data}
                                    onClick={() => {
                                        handleExportLaborSummaryCsv().catch(() => {
                                            // Export is best-effort; the panel keeps showing the read model.
                                        });
                                    }}
                                >
                                    <Download className="h-3.5 w-3.5" />
                                    Exportar CSV
                                </Button>
                            </div>
                        </CardHeader>
                        <CardContent className="pt-0">
                            {laborSummaryQuery.isLoading && (
                                <p className="text-sm text-muted-foreground py-2">Cargando resumen mensual…</p>
                            )}
                            {laborSummaryQuery.isError && (
                                <p className="text-sm text-muted-foreground py-2">
                                    No se pudo cargar el resumen mensual de mano de obra.
                                </p>
                            )}
                            {laborSummaryQuery.data && laborSummaryQuery.data.technicians.length === 0 && (
                                <p className="text-sm text-muted-foreground py-2">
                                    Sin registros de jornadas este mes.
                                </p>
                            )}
                            {laborSummaryQuery.data && laborSummaryQuery.data.technicians.length > 0 && (
                                <div className="space-y-2">
                                    {!reconcileMonthlyLaborSummary(laborSummaryQuery.data).ok && (
                                        <p className="text-xs font-medium text-amber-700">
                                            Aviso: el resumen no reconcilia al 100% para algún técnico.
                                        </p>
                                    )}
                                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
                                        {laborSummaryQuery.data.technicians.map((person) => (
                                            <details
                                                key={person.technicianId}
                                                className="group/technician rounded-lg border bg-card p-3"
                                            >
                                                <summary className="flex cursor-pointer list-none items-center justify-between gap-2 rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                                                    <span className="flex items-center gap-1.5 text-sm font-semibold truncate">
                                                        <User className="h-3.5 w-3.5 shrink-0" />
                                                        {person.technicianName}
                                                    </span>
                                                    <span className="flex items-center gap-1 text-xs font-medium shrink-0">
                                                        <Clock className="h-3.5 w-3.5" />
                                                        {formatLaboralMinutes(person.totalMinutes)}
                                                        {" · "}
                                                        {person.totalDays} {person.totalDays === 1 ? "día" : "días"}
                                                    </span>
                                                    <span className="flex shrink-0 items-center gap-1 text-xs text-ods-navigation">
                                                        <span className="group-open/technician:hidden">Ver desglose</span>
                                                        <span className="hidden group-open/technician:inline">Ocultar</span>
                                                        <ChevronDown aria-hidden="true" className="h-4 w-4 transition-transform group-open/technician:rotate-180" />
                                                    </span>
                                                </summary>
                                                <div className="mt-3 flex flex-col gap-2">
                                                <div className="space-y-1">
                                                    {person.installations.map((inst) => (
                                                        <div
                                                            key={inst.installationId}
                                                            className="flex flex-wrap items-center justify-between gap-x-2 gap-y-0.5 rounded border px-2 py-1 text-xs"
                                                        >
                                                            <span className="min-w-0">
                                                                <span className="block truncate font-medium" title={formatLaborInstallationIdentity(inst).primary}>
                                                                    {formatLaborInstallationIdentity(inst).primary}
                                                                </span>
                                                                <span className="block truncate text-muted-foreground" title={formatLaborInstallationIdentity(inst).secondary ?? undefined}>
                                                                    {formatLaborInstallationIdentity(inst).secondary}
                                                                </span>
                                                            </span>
                                                            <span className="flex w-full min-w-0 max-w-full flex-wrap items-center gap-x-2 gap-y-1 text-muted-foreground sm:w-auto sm:shrink-0 sm:flex-nowrap">
                                                                <span className="whitespace-nowrap">{inst.actualDays} reales · {inst.plannedDays} planificados</span>
                                                                <span className="flex items-center gap-0.5 whitespace-nowrap">
                                                                    <Clock className="h-3 w-3 shrink-0" />
                                                                    {formatLaboralMinutes(inst.totalMinutes)}
                                                                </span>
                                                                <span className="whitespace-nowrap font-semibold text-foreground">
                                                                    {formatLaborPercent(inst.percentOfTechnicianMonth)}
                                                                </span>
                                                            </span>
                                                            <div className="mt-1 w-full basis-full space-y-0.5 border-l-2 border-muted pl-2 text-[11px] text-muted-foreground">
                                                                {inst.days.map((day) => {
                                                                    const dayDate = new Date(`${day.date}T12:00:00`);
                                                                    const dayLabel = Number.isNaN(dayDate.getTime())
                                                                        ? day.date
                                                                        : format(dayDate, "dd/MM", { locale: es });
                                                                    return (
                                                                        <div
                                                                            key={`${inst.installationId}-${day.date}`}
                                                                            className="flex items-center justify-between gap-2"
                                                                            title={formatLaborProvenanceLabel(day.source)}
                                                                        >
                                                                            <span>{dayLabel}</span>
                                                                            <span className="text-right font-medium text-foreground">
                                                                                {day.planned
                                                                                    ? "Planificada"
                                                                                    : day.durationVerified
                                                                                        ? formatLaboralMinutes(day.minutes) + " reales"
                                                                                        : "Duración no verificada"}
                                                                                {day.equivalentMinutes > 0
                                                                                    ? " · equivalencia histórica " + formatLaboralMinutes(day.equivalentMinutes)
                                                                                    : ""}
                                                                            </span>
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                                {(person.plannedAssignments > 0 || person.equivalentMinutes > 0) && (
                                                    <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-muted-foreground">
                                                        {person.plannedAssignments > 0 && (
                                                            <span>{person.plannedAssignments} asignaciones planificadas, fuera del tiempo real</span>
                                                        )}
                                                        {person.equivalentMinutes > 0 && (
                                                            <span>Equivalencia histórica no verificada: {formatLaboralMinutes(person.equivalentMinutes)}, excluida del total real</span>
                                                        )}
                                                    </div>
                                                )}
                                                {person.provenanceCounts.active_segment + person.provenanceCounts.equivalent_day > 0 && (
                                                    <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-muted-foreground">
                                                        {person.provenanceCounts.active_segment > 0 && (
                                                            <span>{person.provenanceCounts.active_segment} tramos activos incluidos</span>
                                                        )}
                                                        {person.provenanceCounts.equivalent_day > 0 && (
                                                            <span>
                                                                {person.provenanceCounts.equivalent_day}{" "}
                                                                {person.provenanceCounts.equivalent_day === 1 ? "día" : "días"} · {formatLaborProvenanceLabel("equivalent_day")}
                                                            </span>
                                                        )}
                                                    </div>
                                                )}
                                                </div>
                                            </details>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                    </details>
                )}

                {/* CAL-05: basic monthly summary by person (Laboral, viewers without CAL-09 access) */}
                {calendarMode === "laboral" && !canViewLaborSummary && (
                    <details className="group rounded-lg border bg-card">
                        <summary className="flex cursor-pointer list-none items-center justify-between gap-3 rounded-md px-3 py-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                            <span className="min-w-0">
                                <span className="block font-semibold">Resumen mensual por persona — {format(currentDate, 'MMMM yyyy', { locale: es })}</span>
                                <span className="block text-xs text-muted-foreground">{laboralMonthSummary.length} técnicos · horas reales verificadas; planificación y equivalencias separadas</span>
                            </span>
                            <span className="flex shrink-0 items-center gap-1 text-sm font-medium text-ods-navigation">
                                <span className="group-open:hidden">Desplegar resumen</span>
                                <span className="hidden group-open:inline">Plegar resumen</span>
                                <ChevronDown aria-hidden="true" className="h-4 w-4 transition-transform group-open:rotate-180" />
                            </span>
                        </summary>
                    <Card className="shrink-0 border-0 shadow-none">
                        <CardHeader className="pb-2">
                            <CardTitle className="text-base font-semibold">
                                Resumen mensual por persona — {format(currentDate, 'MMMM yyyy', { locale: es })}
                            </CardTitle>
                            <p className="text-xs text-muted-foreground">
                                Jornadas, días y horas reales registradas por técnico.
                            </p>
                        </CardHeader>
                        <CardContent className="pt-0">
                            {laboralMonthSummary.length === 0 ? (
                                <p className="text-sm text-muted-foreground py-2">
                                    Sin registros de jornadas este mes.
                                </p>
                            ) : (
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                                    {laboralMonthSummary.map((person) => (
                                        <details
                                            key={person.technicianId}
                                            className="group/technician rounded-lg border bg-card p-3"
                                        >
                                            <summary className="flex cursor-pointer list-none items-center justify-between gap-2 rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                                                <span className="flex min-w-0 items-center gap-1.5 text-sm font-semibold truncate">
                                                    <User className="h-3.5 w-3.5 shrink-0" />
                                                    {person.technicianName}
                                                </span>
                                                <span className="flex shrink-0 items-center gap-1 text-xs font-medium">
                                                    <Clock className="h-3.5 w-3.5" />{formatLaboralMinutes(person.totalMinutes)}
                                                    <span className="ml-1 text-muted-foreground">· {person.days} {person.days === 1 ? "día" : "días"}</span>
                                                </span>
                                                <span className="flex shrink-0 items-center gap-1 text-xs text-ods-navigation">
                                                    <span className="group-open/technician:hidden">Ver desglose</span>
                                                    <span className="hidden group-open/technician:inline">Ocultar</span>
                                                    <ChevronDown aria-hidden="true" className="h-4 w-4 transition-transform group-open/technician:rotate-180" />
                                                </span>
                                            </summary>
                                            <div className="mt-2 flex flex-col gap-1">
                                            <span className="text-xs text-muted-foreground">
                                                {person.days} días con ejecución verificada · {person.assignments} actividades medidas
                                            </span>
                                            <div className="space-y-1">
                                                {person.installations.map((inst) => {
                                                    const identity = formatLaborInstallationIdentity(inst);
                                                    return <div key={inst.installationId} className="border-l-2 border-muted pl-2 text-xs">
                                                        <span className="block font-medium">{identity.primary}</span>
                                                        {identity.secondary && <span className="block text-muted-foreground">{identity.secondary}</span>}
                                                        <span className="text-muted-foreground">{inst.assignments} medidas · {formatLaboralMinutes(inst.totalMinutes)} reales</span>
                                                        {inst.plannedAssignments > 0 && <span className="text-muted-foreground"> · {inst.plannedAssignments} planificadas</span>}
                                                        {inst.equivalentMinutes > 0 && <span className="block text-muted-foreground">Equivalencia histórica no verificada: {formatLaboralMinutes(inst.equivalentMinutes)}</span>}
                                                    </div>;
                                                })}
                                            </div>
                                            {(person.plannedAssignments > 0 || person.equivalentMinutes > 0) && (
                                                <span className="text-[11px] text-muted-foreground">
                                                    {person.plannedAssignments > 0 ? "Planificadas: " + person.plannedAssignments : ""}
                                                    {person.plannedAssignments > 0 && person.equivalentMinutes > 0 ? " · " : ""}
                                                    {person.equivalentMinutes > 0 ? "Equivalencia histórica no verificada: " + formatLaboralMinutes(person.equivalentMinutes) : ""}
                                                </span>
                                            )}
                                            </div>
                                        </details>
                                    ))}
                                </div>
                            )}
                        </CardContent>
                    </Card>
                    </details>
                )}

                {/* Accessible Day Drawer / Popover for Month View "+N más" */}
                <Sheet
                    open={dayDrawerState.isOpen}
                    onOpenChange={(open) => setDayDrawerState((prev) => ({ ...prev, isOpen: open }))}
                >
                    <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto flex flex-col p-6">
                        <SheetHeader className="pb-4 border-b">
                            <SheetTitle className="text-xl font-bold flex items-center gap-2">
                                <CalendarIcon className="h-5 w-5 text-primary" />
                                {dayDrawerState.date
                                    ? format(dayDrawerState.date, "d 'de' MMMM, yyyy", { locale: es })
                                    : "Eventos del día"}
                            </SheetTitle>
                            <SheetDescription className="text-sm text-muted-foreground">
                                {calendarMode === "real"
                                    ? dayDrawerState.events.length === 1
                                        ? "1 evento con evidencia real en esta fecha"
                                        : `${dayDrawerState.events.length} eventos con evidencia real en esta fecha`
                                    : dayDrawerState.events.length === 1
                                        ? "1 evento programado para esta fecha"
                                        : `${dayDrawerState.events.length} eventos programados para esta fecha`}
                            </SheetDescription>
                        </SheetHeader>

                        <div className="flex-1 py-4 space-y-3 overflow-y-auto">
                            {dayDrawerState.events.length === 0 ? (
                                <p className="text-center text-sm text-muted-foreground py-8">
                                    No hay eventos para esta fecha.
                                </p>
                            ) : (
                                dayDrawerState.events.map((evt) => {
                                    const res = evt.resource || {};
                                    // CAL-08: Real events carry their own real-status presentation,
                                    // never workday ("Jornada terminada", "Trabajando"...) semantics.
                                    const statusPresentation = calendarMode === "real"
                                        ? (res.realStatus || getRealStatusPresentation({
                                            realStartDate: res.realStartDateRaw,
                                            realEndDate: res.realEndDateRaw,
                                        }))
                                        : getStatusPresentation(calendarMode === "planning" ? "planning" : "laboral", res.status);
                                    const installationColor = res.installationColor || getInstallationColor(res.installationId);
                                    const realStartLabel = formatRealTimestampLabel(res.realStartDateRaw);
                                    const realEndLabel = formatRealTimestampLabel(res.realEndDateRaw);

                                    return (
                                        <div
                                            key={evt.id}
                                            className="p-3.5 rounded-lg border bg-card text-card-foreground shadow-xs hover:shadow-md transition-shadow space-y-2.5"
                                            style={{ borderLeft: `4px solid ${installationColor}` }}
                                        >
                                            <div className="flex items-start justify-between gap-2">
                                                <div className="space-y-0.5 min-w-0">
                                                    <h4 className="font-semibold text-sm leading-tight truncate">
                                                        {calendarMode === "laboral" ? (res.siteLabel || `Instalación #${res.installationId}`) : (res.clientName || evt.title || "Instalación")}
                                                    </h4>
                                                    {calendarMode === "laboral" && res.siteContext && (
                                                        <p className="text-xs text-muted-foreground truncate">{res.siteContext}</p>
                                                    )}
                                                    {res.phaseTitle && (
                                                        <p className="text-xs text-muted-foreground truncate">
                                                            Fase: {res.phaseTitle}
                                                        </p>
                                                    )}
                                                    {calendarMode === "real" && res.realSessionLabel && (
                                                        <p className="text-xs text-muted-foreground truncate">
                                                            {res.realSessionLabel}
                                                        </p>
                                                    )}
                                                </div>
                                                <div className="flex items-center gap-1 shrink-0">
                                                    <Badge variant="outline" className={`text-xs ${statusPresentation.badgeClass}`}>
                                                        {statusPresentation.label}
                                                    </Badge>
                                                    {res.isContractual && (
                                                        <Badge variant="destructive" className="text-xs font-semibold">
                                                            Contractual
                                                        </Badge>
                                                    )}
                                                </div>
                                            </div>

                                            {res.isContractual && res.contractCode && res.contractualEndDate && (
                                                <div className="text-xs text-red-600 dark:text-red-400 space-y-0.5" role="note">
                                                    <p className="font-semibold">⚠ Compromiso contractual</p>
                                                    <p>Contrato: {res.contractCode}</p>
                                                    <p>Fecha límite: {new Date(`${res.contractualEndDate}T00:00:00`).toLocaleDateString("es-ES")}</p>
                                                </div>
                                            )}

                                            {res.description && (
                                                <p className="text-xs text-muted-foreground line-clamp-2">
                                                    {res.description}
                                                </p>
                                            )}

                                            {calendarMode === "real" && (
                                                <div className="space-y-1.5 text-xs text-muted-foreground pt-1 border-t">
                                                    {realStartLabel && (
                                                        <p className="flex items-center gap-1 pt-1">
                                                            <Clock className="h-3.5 w-3.5" />
                                                            Inicio real: {realStartLabel}
                                                        </p>
                                                    )}
                                                    <p className="flex items-center gap-1">
                                                        <Clock className="h-3.5 w-3.5" />
                                                        {res.realEndDateRaw
                                                            ? `Fin real: ${realEndLabel}`
                                                            : res.inProgress
                                                                ? "Fin real: En curso"
                                                                : res.closureUnverified
                                                                    ? statusPresentation.label
                                                                    : "Fin real: Sin registro"}
                                                    </p>
                                                    {Array.isArray(res.technicians) && res.technicians.length > 0 && (
                                                        <p className="flex items-start gap-1">
                                                            <User className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                                                            <span>Técnicos: {res.technicians.join(", ")}</span>
                                                        </p>
                                                    )}
                                                    {typeof res.totalRealMinutes === "number" && (
                                                        <p className="flex items-center gap-1">
                                                            <Clock className="h-3.5 w-3.5" />
                                                            Minutos reales agregados: {Math.floor(res.totalRealMinutes / 60)}h {res.totalRealMinutes % 60}m
                                                        </p>
                                                    )}
                                                    {(res.plannedStartLabel || res.plannedEndLabel) && (
                                                        <p>
                                                            Planificado: {[res.plannedStartLabel, res.plannedEndLabel].filter(Boolean).join(" → ")}
                                                        </p>
                                                    )}
                                                    {res.diffSummary && (
                                                        <p className="font-medium text-foreground">{res.diffSummary}</p>
                                                    )}
                                                </div>
                                            )}

                                            {calendarMode === "laboral" && (
                                                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground pt-1">
                                                    {res.technicianName && (
                                                        <span className="flex items-center gap-1">
                                                            <User className="h-3.5 w-3.5" />
                                                            {res.technicianName}
                                                        </span>
                                                    )}
                                                    {res.address && (
                                                        <span className="flex items-center gap-1 min-w-0">
                                                            <MapPin className="h-3.5 w-3.5 shrink-0" />
                                                            <span className="truncate">{res.address}</span>
                                                        </span>
                                                    )}
                                                    {res.realTimeLabel && (
                                                        <span className="flex items-center gap-1">
                                                            <Clock className="h-3.5 w-3.5" />
                                                            {res.realTimeLabel}
                                                        </span>
                                                    )}
                                                    {res.planned ? (
                                                        <span>Asignación planificada; sin tiempo real registrado</span>
                                                    ) : typeof res.durationMinutes === "number" ? (
                                                        <span className="flex items-center gap-1 font-medium text-foreground">
                                                            <Clock className="h-3.5 w-3.5" />
                                                            Tiempo real: {formatLaboralMinutes(res.durationMinutes)}
                                                        </span>
                                                    ) : (
                                                        <span>Duración no verificada</span>
                                                    )}
                                                    {res.equivalentMinutes > 0 && (
                                                        <span>Equivalencia histórica no verificada: {formatLaboralMinutes(res.equivalentMinutes)}; excluida del tiempo real</span>
                                                    )}
                                                </div>
                                            )}

                                            <Button
                                                size="sm"
                                                variant={calendarMode === "laboral" ? "laborAction" : "default"}
                                                className="w-full mt-2 text-xs font-medium justify-between"
                                                onClick={() => {
                                                    handleEventClick(evt);
                                                    setDayDrawerState((prev) => ({ ...prev, isOpen: false }));
                                                }}
                                            >
                                                <span>Ver detalle de instalación</span>
                                                <ArrowRight className="h-3.5 w-3.5 ml-1" />
                                            </Button>
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    </SheetContent>
                </Sheet>
            </div>
        </DashboardLayout>
    );
}
